import React, { useState, useEffect } from 'react';
import { Key, RefreshCw, AlertTriangle } from 'lucide-react';
import { getEncryptionKeys, rotateEncryptionKey } from '../../../services/complianceService';
import Button from '../../../components/ui/Button';

/**
 * Encryption Key Management Panel
 * SOC 2 control for key rotation
 */
const EncryptionKeyPanel = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadKeys();
  }, []);
  
  const loadKeys = async () => {
    setLoading(true);
    const result = await getEncryptionKeys();
    if (result?.success) {
      setKeys(result?.data || []);
    }
    setLoading(false);
  };
  
  const handleRotate = async (keyId, keyName) => {
    if (!confirm(`Are you sure you want to rotate the encryption key "${keyName}"? This is a critical security operation.`)) return;
    
    const result = await rotateEncryptionKey(keyId);
    if (result?.success) {
      alert('Encryption key rotated successfully');
      loadKeys();
    } else {
      alert(`Rotation failed: ${result?.error}`);
    }
  };
  
  const isOverdue = (nextRotationDate) => {
    return new Date(nextRotationDate) <= new Date();
  };
  
  const getDaysUntilRotation = (nextRotationDate) => {
    const days = Math.ceil((new Date(nextRotationDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading encryption keys...</div>
        ) : keys?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No encryption keys found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {keys?.map(key => {
              const daysUntil = getDaysUntilRotation(key?.next_rotation_date);
              const overdue = isOverdue(key?.next_rotation_date);
              
              return (
                <div key={key?.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      overdue ? 'bg-red-100' : daysUntil <= 7 ? 'bg-yellow-100' : 'bg-green-100'
                    }`}>
                      <Key className={`w-5 h-5 ${
                        overdue ? 'text-red-600' : daysUntil <= 7 ? 'text-yellow-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{key?.key_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {key?.key_type?.toUpperCase()} | Purpose: {key?.key_purpose}
                          </p>
                        </div>
                        {overdue && (
                          <span className="ml-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertTriangle className="w-3 h-3" />
                            Overdue
                          </span>
                        )}
                      </div>
                      
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Last Rotated:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {new Date(key?.last_rotated_at)?.toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Next Rotation:</span>
                          <span className={`ml-2 font-medium ${
                            overdue ? 'text-red-600' : daysUntil <= 7 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {new Date(key?.next_rotation_date)?.toLocaleDateString()}
                            {!overdue && ` (${daysUntil} days)`}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Rotation Schedule:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            Every {key?.rotation_schedule_days} days
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Status:</span>
                          <span className={`ml-2 font-medium ${
                            key?.is_active ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {key?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <Button
                          onClick={() => handleRotate(key?.id, key?.key_name)}
                          size="sm"
                          variant={overdue ? 'danger' : 'outline'}
                          className="inline-flex items-center gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Rotate Key Now
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EncryptionKeyPanel;